import type { ReactNode } from "react";
import { Button } from "@repo/components";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { VenueSwitcher } from "./VenueSwitcher";
import { LiveIndicator } from "./LiveIndicator";
import { ActingStaffBadge } from "./ActingStaffBadge";
import { LanguageToggle } from "./LanguageToggle";
import { ShellIcon } from "./icons";

/**
 * The app header: the mobile-nav hamburger (below md), venue switcher, live indicator, the PIN
 * quick-switch mount slot + acting-staff attribution badge, and the user menu. [pinQuickSwitch] is the
 * named slot the PIN dialog (roles-and-permissions) mounts into. [onMenuToggle]/[navOpen] drive the
 * off-canvas SideNav drawer on phones. Everything is built on `@repo/components` primitives.
 */
export function AppHeader({
  pinQuickSwitch,
  navOpen = false,
  onMenuToggle,
}: {
  pinQuickSwitch?: ReactNode;
  navOpen?: boolean;
  onMenuToggle?: () => void;
}) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  return (
    <header
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: { base: "2", md: "4" },
        px: "4",
        py: "2",
        borderBottomWidth: "1px",
        borderColor: "border.default",
      })}
    >
      <div className={css({ display: "flex", alignItems: "center", gap: { base: "2", md: "4" } })}>
        {/* Hamburger — opens the SideNav drawer; only shown below md (static sidebar handles md+). */}
        <button
          type="button"
          onClick={onMenuToggle}
          aria-label={navOpen ? t("shell.nav.close") : t("shell.nav.open")}
          aria-expanded={navOpen}
          aria-controls="app-sidenav"
          className={css({
            display: { base: "inline-flex", md: "none" },
            alignItems: "center",
            justifyContent: "center",
            minW: "44px",
            minH: "44px",
            fontSize: "xl",
            color: "fg.default",
            rounded: "md",
            cursor: "pointer",
            _hover: { bg: "bg.muted" },
          })}
        >
          <ShellIcon name={navOpen ? "close" : "menu"} />
        </button>
        <VenueSwitcher />
        <LiveIndicator />
      </div>
      <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
        {/* PIN quick-switch slot (roles-and-permissions mounts its dialog here). */}
        <div data-slot="pin-quick-switch">{pinQuickSwitch}</div>
        <ActingStaffBadge />
        <LanguageToggle />
        {user?.email ? (
          // The email is contextual, not essential — hide it below md so the header never overflows.
          <span
            className={css({
              display: { base: "none", md: "inline" },
              fontSize: "sm",
              color: "fg.muted",
            })}
          >
            {user.email}
          </span>
        ) : null}
        <Button size="sm" variant="ghost" onClick={logout}>
          {t("shell.signOut")}
        </Button>
      </div>
    </header>
  );
}
